import React from 'react';
import DOMPurify from 'dompurify';

type CleanedHtmlProps = {
  as?: React.ElementType<any>;
  className?: string;
}

// Allow links to have the target attribute; allow iframes for Custom Widget embeds (YouTube, etc.)
// https://github.com/cure53/DOMPurify/issues/317#issuecomment-470429778
if (typeof DOMPurify.setConfig === 'function') {
  DOMPurify.setConfig({
    ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'referrerpolicy', 'frameborder'],
    ADD_TAGS: ['iframe'],
  });
}

/** Sanitizes HTML with the same settings widgets use on the page. */
export const sanitizeHtml = (html: string): string => (
  typeof DOMPurify.sanitize === 'function' ? DOMPurify.sanitize(html) : html
);

function createMarkup({ children }) {
  if (typeof DOMPurify.sanitize === 'function') {
    return { __html: DOMPurify.sanitize(children) };
  }
  // SSR fallback: DOMPurify is unsupported without a full DOM
  return { __html: children };
}

function CleanedHtml(props: React.PropsWithChildren<CleanedHtmlProps>) {
  if (typeof props.children === 'string') {
    const { as: Component, children, className } = props;
    // Creates a dynamic element based on the "as" prop.
    // Sanitizes and set dangerous inner html.
    return (
      <Component
        className={className}
        dangerouslySetInnerHTML={createMarkup({ children })}
      />
    );
  }

  // Return a React element by wrapping children in a fragment
  return <>{props.children}</>;
}

CleanedHtml.defaultProps = {
  as: 'div',
};

export default React.memo(CleanedHtml);
